/* eslint-disable react-hooks/exhaustive-deps */
import { useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';

import { CountryData } from '@components/country-code/types';
import { ErrorCode } from '@constants/errorCodes';
import {
  AccountType,
  GROUP_MEMBER_MAX_AMOUNT,
  GROUP_MEMBER_MIN_AMOUNT,
  NAME_MAX_LENGTH,
  NAME_MIN_LENGTH,
  SubscriptionPlanExpiredMessage,
  SubscriptionStatus
} from '@constants/generic';
import UserPermissionTypes from '@constants/permissions';
import { GROUPS, SUBSCRIPTIONS } from '@constants/routes';
import { STORAGE_KEYS } from '@constants/storage';
import { Event, sendMixpanelEvent } from '@core/analytics/mixpanel';
import { updateGroupInfo } from '@features/group-management/slices/groupSlice';
import { AddMemberModeType } from '@features/group-management/types';
import useNotify from '@hooks/notify';
import {
  AddMemberModalType,
  ApiError,
  MemberListType,
  MemberValidationResult
} from '@types';
import { buildQueryString } from '@utils/queryStringBuilder';
import {
  getLocalStorageItem,
  getSessionStorageItem,
  setSessionStorageItem
} from '@utils/storage';
import { capitalizeFirstLetter } from '@utils/stringFormat';
import { TabPill } from 'types/tabs';

import {
  AddMembersTabType,
  addNewGroupTabs as tabs,
  groupInfoAddMemberTableColumn,
  PaymentCollectionType
} from '../constants';
import {
  getPaymentCollectionType,
  getTermsListFromConfig,
  invalidCSVErrorMessage,
  isInvalidMemberList
} from '../helper';
import {
  getCustomTermPastDueStatus,
  getMemberListPastDueStatus,
  zeroPastDueTermAmounts
} from '../helpers/customTerm';
import { transformMembersArray } from '../pages/v1/add-members/helper';
import {
  useAddMemberToGroupV3Mutation,
  useLazyGetGroupByIdQuery
} from '../queries/GroupQuery';

const useAddMembersStep = (groupId: string) => {
  const navigate = useNavigate();
  const dispatch = useDispatch();

  const groupDetails = useSelector(
    (state: any) => state.group.currentGroupInfo
  );
  const {
    orgConfig,
    accountType,
    appConfig,
    rolesAndPermissions,
    currentSubscription
  } = useSelector((state: any) => state.user);
  const { memberCustomFields } = useSelector(
    (state: any) => state.branchConfig
  );

  const groupMemberMaxAmount =
    orgConfig?.group_member_amount_max || GROUP_MEMBER_MAX_AMOUNT;
  const groupMemberMinAmount =
    orgConfig?.group_member_amount_min || GROUP_MEMBER_MIN_AMOUNT;
  const memberNameMaxLength =
    orgConfig?.member_name_length_max || NAME_MAX_LENGTH;
  const memberNameMinLength =
    orgConfig?.member_name_length_min || NAME_MIN_LENGTH;

  const { createErrorAlert, createSuccessAlert } = useNotify();

  const groupAddMemberTabs = tabs(
    accountType?.type === AccountType.ORGANIZATION
  );

  const [selectedTab, setSelectedTab] = useState<TabPill>(
    groupAddMemberTabs[0]
  );
  const [tabWaitingToSwitch, setTabWaitingToSwitch] = useState<TabPill | null>(
    null
  );
  const [isAddMemberModalOpen, setIsAddMemberModalOpen] =
    useState<boolean>(false);
  const [memberList, setMemberList] = useState<MemberListType[]>([]);
  const [selectedMember, setSelectedMember] = useState<MemberListType | null>();
  const [isEditMemberModalOpen, setIsEditMemberModalOpen] =
    useState<boolean>(false);
  const [selectedRows, setSelectedRows] = useState<string[]>([]);

  const [showPastDueDateModal, setShowPastDueDateModal] =
    useState<boolean>(false);
  const [isAllDueDatePast, setIsAllDueDatePast] = useState<boolean>(false);

  const [addMembersV3, { isLoading: isAddMembersV3Loading }] =
    useAddMemberToGroupV3Mutation();
  const [getGroupById, { data: currentGroupDetails }] =
    useLazyGetGroupByIdQuery();

  const addMemberModeRef = useRef<AddMemberModeType>('File');
  const memberListRef = useRef<MemberListType[]>(memberList);
  memberListRef.current = memberList;

  const organizationId = getLocalStorageItem(STORAGE_KEYS.ORGANIZATION_ID);
  const branchId = getLocalStorageItem(STORAGE_KEYS.BRANCH_ID);

  const getApiParams = () => ({
    organizationId,
    branchId,
    groupId: groupId || ''
  });

  const countryCodeMapping = (
    appConfig?.supported_phone_number_countries || []
  ).reduce((acc: Record<string, string>, country: CountryData) => {
    acc[country.dial_code.toString()] = country.code;
    return acc;
  }, {});

  const HOME_PATH = GROUPS.HOME;

  const goToGroupInfo = () => {
    if (
      `${groupDetails.name}`.trim() ===
      `${getSessionStorageItem(STORAGE_KEYS.NEW_GROUP_NAME)}`.trim()
    ) {
      setSessionStorageItem(STORAGE_KEYS.NEW_GROUP_NAME, '');
      setSessionStorageItem(STORAGE_KEYS.SHOW_COFEE_INTRO_MODAL, true);
    }
    navigate(`${HOME_PATH}/${groupId}`);
  };

  const openAddMemberModal = () => {
    setIsAddMemberModalOpen(true);
  };

  const onSelectTab = (tab: TabPill) => {
    if (memberList.length) {
      setTabWaitingToSwitch(tab);
    } else {
      setSelectedTab(tab);
    }
  };

  const onTabSwitchConfirm = () => {
    setSelectedTab(tabWaitingToSwitch as TabPill);
    setMemberList([]);
    setSelectedRows([]);
    setTabWaitingToSwitch(null);
  };

  const onTabSwitchCancel = () => {
    setTabWaitingToSwitch(null);
  };

  const handleAddMembers = (item: MemberListType) => {
    addMemberModeRef.current = 'Manual';
    const updatedList = [...memberList, item];
    setMemberList(updatedList);
  };

  const handleEditMember = (memberId: string) => {
    setIsEditMemberModalOpen(true);
    const member = memberList.find((item) => item.id === memberId);
    setSelectedMember(member);
  };

  const onEditMemberSave = (member: MemberListType) => {
    const memberArray = [...memberList];
    const memberIndex = memberArray.findIndex((item) => item.id === member.id);
    if (memberIndex >= 0) {
      memberArray[memberIndex] = member;
      setMemberList(memberArray);
      setSelectedMember(null);
    }
  };

  const handleDeleteMember = (memberId: string) => {
    const updatedMemberList = memberList.filter(
      (member) => member.id !== memberId
    );
    setMemberList(updatedMemberList);
  };

  const handleAddMemberModalCancel = () => setIsAddMemberModalOpen(false);
  const handleEditMemberModalCancel = () => setIsEditMemberModalOpen(false);

  const isImmediatePayment =
    groupDetails?.isImmediatePayment ||
    currentGroupDetails?.interval_execution_type === 'immediate';

  const isCustomTerm =
    groupDetails?.isCustomTerm ||
    currentGroupDetails?.payment_schedule?.interval ===
      PaymentCollectionType.CUSTOM_TERM;

  const isAttendanceBasedFee =
    groupDetails?.isAttendanceBasedFee ||
    !!currentGroupDetails?.attendance_config?.is_attendance_based_fee_enabled;

  const validateMemberWithApi = async (
    memberData: MemberListType
  ): Promise<MemberValidationResult> => {
    try {
      const { transformedMembersArray } = transformMembersArray(
        [memberData],
        groupDetails,
        currentGroupDetails,
        isImmediatePayment,
        isCustomTerm,
        false,
        false,
        false,
        0,
        memberCustomFields || []
      );

      const response = await addMembersV3({
        params: getApiParams(),
        body: {
          members_added: transformedMembersArray,
          force_create: false
        }
      });

      if ('data' in response && response.data?.data?.members_added) {
        const membersAdded = response.data.data.members_added;

        const memberWithError = membersAdded.members?.[0];
        if (memberWithError?.errors && memberWithError.errors.length > 0) {
          const errorMessages = memberWithError.errors
            .map((e: { field: string; message: string }) => e.message)
            .join(', ');
          return {
            hasError: true,
            hasWarning: false,
            errorMessage: errorMessages
          };
        }

        const warningRecord = membersAdded.warnings?.[0];
        if (warningRecord) {
          const admissionNumber = warningRecord.admission_number || '';
          const warningField = warningRecord.errors?.[0]?.field;
          return {
            hasError: false,
            hasWarning: true,
            warningMessage: 'Member already exists',
            admissionNumber,
            isAdmissionNumberError:
              warningField === 'admission_number' && !!admissionNumber
          };
        }

        return { hasError: false, hasWarning: false };
      }

      if ('error' in response) {
        const error = response.error as ApiError;
        const errorMessage = error.data?.errors?.length
          ? error.data?.errors[0].message
          : error.data?.message || 'Validation failed';
        return {
          hasError: true,
          hasWarning: false,
          errorMessage: capitalizeFirstLetter(errorMessage)
        };
      }

      return { hasError: false, hasWarning: false };
    } catch {
      return {
        hasError: true,
        hasWarning: false,
        errorMessage: 'Validation failed. Please try again.'
      };
    }
  };

  const executeSaveMembers = async (membersToSave: MemberListType[]) => {
    const isFileUpload = addMemberModeRef.current === 'File';

    const { transformedMembersArray } = transformMembersArray(
      membersToSave,
      groupDetails,
      currentGroupDetails,
      isImmediatePayment,
      isCustomTerm,
      isFileUpload,
      false,
      false,
      0,
      memberCustomFields || []
    );

    const payload = {
      params: getApiParams(),
      body: {
        members_added: transformedMembersArray,
        force_create: true
      }
    };
    const res = await addMembersV3(payload);

    if ('data' in res && res.data?.status) {
      const successToastMessage = `${
        transformedMembersArray.length === 1 ? 'Member' : 'Members'
      } added successfully`;
      createSuccessAlert(successToastMessage);
      goToGroupInfo();
      sendMixpanelEvent(Event.MEMBER_ADDITION_TO_EXISTING_GROUP_SUCCESS, {
        add_member_mode: addMemberModeRef.current,
        group_name: groupDetails.name || currentGroupDetails?.name,
        members: transformedMembersArray
      });
    } else if ('error' in res) {
      const error = res.error as ApiError;
      const errorMessage = error.data?.errors?.length
        ? error.data?.errors[0].message
        : error.data?.message;
      createErrorAlert(
        capitalizeFirstLetter(errorMessage || 'Error while adding member')
      );
      if (error?.data?.errorCode === ErrorCode.ERR_SUBSCRIPTION_EXPIRED) {
        if (
          rolesAndPermissions.permissions.includes(
            UserPermissionTypes.SUBSCRIPTION_VIEW
          )
        ) {
          navigate(SUBSCRIPTIONS.PLAN_DETAILS);
        } else {
          navigate(`${HOME_PATH}/${groupId}`);
        }
      }
      sendMixpanelEvent(Event.MEMBER_ADDITION_TO_EXISTING_GROUP_ERROR, {
        add_member_mode: addMemberModeRef.current,
        group_name: groupDetails.name || currentGroupDetails?.name,
        error: error.data?.errorCode
      });
    }
  };

  const onSaveMembers = async () => {
    const currentMemberList = memberListRef.current;

    if (currentSubscription?.status === SubscriptionStatus.EXPIRED) {
      createErrorAlert(SubscriptionPlanExpiredMessage);
      if (
        rolesAndPermissions.permissions.includes(
          UserPermissionTypes.SUBSCRIPTION_VIEW
        )
      ) {
        navigate(SUBSCRIPTIONS.PLAN_DETAILS);
      }
    } else {
      if (
        isInvalidMemberList(
          currentMemberList,
          groupMemberMaxAmount,
          groupMemberMinAmount,
          memberNameMaxLength,
          memberNameMinLength,
          countryCodeMapping
        )
      ) {
        createErrorAlert(
          selectedTab.id === AddMembersTabType.IMPORT_FILE
            ? invalidCSVErrorMessage
            : 'Invalid member data'
        );
        return;
      }

      if (
        isCustomTerm &&
        selectedTab.id === AddMembersTabType.ADD_FROM_MEMBER_LIST
      ) {
        const termDetails =
          groupDetails.termsList ||
          currentGroupDetails?.payment_schedule?.term_details;
        if (termDetails?.length) {
          const { hasPastDue, allPastDue } =
            getCustomTermPastDueStatus(termDetails);
          if (hasPastDue) {
            setIsAllDueDatePast(allPastDue);
            setShowPastDueDateModal(true);
            return;
          }
        }
      }

      if (isCustomTerm && selectedTab.id === AddMembersTabType.IMPORT_FILE) {
        const { hasPastDue, allPastDue } =
          getMemberListPastDueStatus(currentMemberList);
        if (hasPastDue) {
          setIsAllDueDatePast(allPastDue);
          setShowPastDueDateModal(true);
          return;
        }
      }

      await executeSaveMembers(currentMemberList);
    }
  };

  const handleConfirmPastDueDate = async () => {
    setShowPastDueDateModal(false);
    const updatedMembers = zeroPastDueTermAmounts(memberListRef.current);
    await executeSaveMembers(updatedMembers);
  };

  const handleCancelPastDueDate = () => {
    setShowPastDueDateModal(false);
  };

  const handleGetGroupById = async () => {
    const queryObject = {
      include_payment_stats: true,
      include_group_stats: true,
      include_payment_details: true,
      include_fee_categories: true
    };
    const queryString = buildQueryString(queryObject);
    const payload = {
      params: getApiParams(),
      queryString
    };
    const res = await getGroupById(payload);

    if (res.data) {
      const {
        frequency,
        interval_frequency: interval,
        name,
        amount,
        next_due_at: dueDate
      } = res.data;
      dispatch(
        updateGroupInfo({
          frequency,
          interval,
          name,
          groupAmount: amount,
          dueDate,
          isFeeCategoryEnabled: res.data?.is_fee_configured,
          feeCategories: res.data?.group_fee_amount_configuration,
          isActive: res.data?.is_active,
          startDate: res.data?.start_date,
          isImmediatePayment: res.data?.interval_execution_type === 'immediate',
          isCustomTerm:
            res.data?.payment_schedule?.interval ===
            PaymentCollectionType.CUSTOM_TERM,
          monthlyConfig: res.data?.payment_schedule?.monthly_config,
          weeklyConfig: res.data?.payment_schedule?.weekly_config,
          termsList: res.data?.payment_schedule?.term_details,
          isAttendanceBasedFee:
            !!res.data?.attendance_config?.is_attendance_based_fee_enabled,
          sessionsPerCycle: res.data?.payment_schedule?.sessions_per_cycle
        })
      );
    }
  };

  const sessionsPerCycle =
    groupDetails?.sessionsPerCycle ||
    currentGroupDetails?.payment_schedule?.sessions_per_cycle;

  const isSessionBased =
    currentGroupDetails?.payment_schedule?.interval ===
      PaymentCollectionType.SESSION_BASED || !!sessionsPerCycle;

  const groupPaymentCollectionType = getPaymentCollectionType(
    groupDetails.frequency || currentGroupDetails?.frequency,
    groupDetails.interval || currentGroupDetails?.interval_frequency
  ) as PaymentCollectionType;

  const groupStartDate =
    groupDetails?.startDate || currentGroupDetails?.start_date;

  const groupEndDate =
    groupDetails?.endDate || currentGroupDetails?.payment_schedule?.end_date;

  const getGroupDueDate = () => {
    const paymentCollectionType = groupPaymentCollectionType;

    if (paymentCollectionType === PaymentCollectionType.ONCE) {
      return groupDetails.dueDate || currentGroupDetails?.next_due_at;
    }
    if (paymentCollectionType === PaymentCollectionType.WEEKLY) {
      return (
        groupDetails.weeklyConfig?.day_of_week ||
        currentGroupDetails?.weeklyConfig?.day_of_week
      );
    }
    if (
      paymentCollectionType === PaymentCollectionType.MONTHLY ||
      paymentCollectionType === PaymentCollectionType.BY_TERM
    ) {
      if (
        groupDetails.monthlyConfig?.day_option === 'custom' ||
        currentGroupDetails?.monthlyConfig?.day_option === 'custom'
      )
        return (
          groupDetails.monthlyConfig?.custom_day?.toString() ||
          currentGroupDetails?.monthlyConfig?.custom_day?.toString()
        );
      return (
        groupDetails.monthlyConfig?.day_option?.toString() ||
        currentGroupDetails?.monthlyConfig?.day_option?.toString()
      );
    }

    return (
      groupDetails.monthlyConfig?.custom_day?.toString() ||
      currentGroupDetails?.monthlyConfig?.custom_day?.toString()
    );
  };

  const getModalData = (type: AddMemberModalType) => ({
    isOpen:
      type === AddMemberModalType.ADD
        ? isAddMemberModalOpen
        : isEditMemberModalOpen,
    handleCancel:
      type === AddMemberModalType.ADD
        ? handleAddMemberModalCancel
        : handleEditMemberModalCancel,
    addMember:
      type === AddMemberModalType.ADD ? handleAddMembers : onEditMemberSave,
    paymentCollectionType: groupPaymentCollectionType,
    type,
    groupAmount: groupDetails.groupAmount || currentGroupDetails?.amount,
    groupDueDate: getGroupDueDate(),
    groupFrequency:
      groupDetails?.interval || currentGroupDetails?.interval_frequency,
    isCategoryEnabled: true
  });

  const memberModalData = {
    add: getModalData(AddMemberModalType.ADD),
    edit: getModalData(AddMemberModalType.EDIT)
  };

  const isSaveDisabled = !memberList.length;

  const handleAddMembersViaFile = (members: MemberListType[]) => {
    addMemberModeRef.current = 'File';
    setMemberList(members);
  };

  const handleAddMembersFromList = (members: MemberListType[]) => {
    const updatedMembers = members.map((member) => ({
      ...member,
      ...(isCustomTerm && {
        termsList: getTermsListFromConfig({
          termsList:
            groupDetails.termsList ||
            currentGroupDetails?.payment_schedule?.term_details
        })
      })
    }));

    addMemberModeRef.current = 'MemberList';
    setMemberList(updatedMembers);
  };

  useEffect(() => {
    setMemberList([]);
  }, [selectedTab]);

  useEffect(() => {
    if (groupId) {
      handleGetGroupById();
    }
  }, [groupId]);

  useEffect(() => {
    if (
      !(groupDetails.isActive || currentGroupDetails?.is_active) &&
      (groupDetails.frequency || currentGroupDetails?.frequency) ===
        PaymentCollectionType.ONCE
    ) {
      navigate(`${GROUPS.HOME}/${groupId}`);
    }
  }, [groupDetails.frequency, currentGroupDetails?.frequency]);

  return {
    selectedTab,
    onSelectTab,
    groupAddMemberTabs,
    memberList,
    selectedMember,
    isAddMemberModalOpen,
    isEditMemberModalOpen,
    selectedRows,
    setSelectedRows,
    openAddMemberModal,
    handleAddMembers,
    handleEditMember,
    handleDeleteMember,
    handleAddMemberModalCancel,
    handleEditMemberModalCancel,
    handleAddMembersViaFile,
    handleAddMembersFromList,
    onSaveMembers,
    validateMemberWithApi,
    isLoading: isAddMembersV3Loading,
    isSaveDisabled,
    memberModalData,
    groupDetails,
    currentGroupDetails,
    groupPaymentCollectionType,
    groupStartDate,
    groupEndDate,
    getGroupDueDate,
    isImmediatePayment,
    isCustomTerm,
    tabWaitingToSwitch,
    onTabSwitchConfirm,
    onTabSwitchCancel,
    groupInfoAddMemberTableColumn,
    onEditMemberSave,
    organizationId,
    branchId,
    isAttendanceBasedFee,
    isSessionBased,
    sessionsPerCycle,
    showPastDueDateModal,
    isAllDueDatePast,
    handleConfirmPastDueDate,
    handleCancelPastDueDate
  };
};

export default useAddMembersStep;
